/**
 * The idle Stage (G5): no game of mine is on the board, but a tournament is
 * open (`railState === 'tournament'`, waiting for a pairing). The board itself
 * still shows the start position, disabled (`selectBoardProps` already yields
 * that for `phase === 'none'` — this unit changes nothing about the board), so
 * this is purely the dimming scrim + the centred status card on top of it.
 *
 * Visibility is `tournament.id` set AND no game of mine AND no watch — the
 * same "idle" half of `rail/Rail/model.ts selectRailMode`'s `noOwnGame`,
 * without its "ended" branch (an ended tournament game is the RESULT overlay,
 * not this card).
 *
 * CORE-ONLY, on purpose: `Stage` (and therefore this unit) mounts for EVERY
 * live game, not only a tournament's — `useTournamentDetails` is a `useQuery`,
 * which throws with no `QueryClientProvider` above it (the SAME rule
 * `rail/live/LiveBody/model.ts` documents for `HintCard`'s mutation), so this
 * file may never call one. `joined` and `isArena` are therefore the cheaper
 * core-only signals `hooks/useTournamentEffects.ts`'s default-tab effect also
 * uses — eventually consistent once `DetailsPanel` / `StandingsPanel` resolve
 * the REST answer (which is also where this falls back TO, via "Join from
 * Details"). One accepted cosmetic gap from this, UNCHANGED by fix S: the
 * "Starts at 7:00 PM" (before round 1) sub-line G5 asked for needs the REST
 * `start_date`, which `tournamentStatusFrom` only reads off `raw` — with
 * `raw: null` here, that one countdown (branches 4/12) is unreachable on
 * this card; everything else (the round's own pairing/break countdowns) is
 * core-only already and renders exactly as it does on the Details tab.
 *
 * H5 REVISED (2026-10-06): the CTA is "Register from Details" now, not
 * "Join from Details" — this card cannot tell a free join from a paid
 * register apart (that needs the REST response only `DetailsPanel` has, the
 * same G5b constraint above), so pressing it both switches to the Details
 * tab AND arms `registrationIntent`, which `DetailsPanel`'s model consumes
 * once its own `cta.kind` has resolved, pressing whichever CTA actually
 * applies — see `registrationIntent.ts`'s own header for the full handoff.
 *
 * Fix S / S7 (2026-10-06): the card's copy now comes from `tournamentStatusFrom`
 * (S1) — `raw: null`, since this stays core-only (see above) — instead of its
 * own branch set, so the board and the Details tab's status card can never
 * disagree (ARCHITECTURE §19). Every kind is one of `TournamentPhase`'s names
 * directly — `myGameLive` is always `false` here, by construction: this card
 * only renders while `idle` (no game of mine, no watch) is true.
 *
 * Fix T / T1 + T5 (2026-10-06): `arenaPaused` RETIRED as a local special
 * case — the status model's own `paused` phase (T1) IS that case now, built
 * through the SAME `statusInputFrom` (T1) `DetailsPanel` uses, so the two
 * can never disagree on the arena INPUT either, not only on the function.
 * The arena phases this card did not have before (`pooled`/`paused`/
 * `sittingOut`-in-an-arena/`ending`) get their CTAs below; `ended` gets an
 * arena-aware action label ("See leaderboard").
 */
import {useShallow} from 'zustand/react/shallow';

import {useLiveGame} from '@/Store/liveGameStore';
import type {LiveGameState} from '@/Store/liveGameStore';

import {resultTabFor} from '../../rail/tabs/tabSpec';
import {showTournamentIdle} from '../../rail/surface';
import {armRegistrationIntent} from '../../rail/tournament/registration/registrationIntent';
import {nowServerMs} from '../../rail/tournament/status/serverClock';
import {formatCountdown} from '../../rail/tournament/status/formatCountdown';
import {statusInputFrom} from '../../rail/tournament/status/statusInput';
import {tournamentStatusFrom} from '../../rail/tournament/status/tournamentStatus';
import type {TournamentPhase} from '../../rail/tournament/status/tournamentStatus';
import {useCountdownSeconds} from '../../rail/tournament/useCountdownSeconds';
import {useV2Ui} from '../../theme/useV2Ui';

export type TournamentIdleKind = 'none' | TournamentPhase;

export type TournamentIdleModel = {
  kind: TournamentIdleKind;
  title: string;
  /** the waiting card's own sub-line (a countdown, or the status detail) */
  subtitle: string;
  actionLabel: string | null;
  onAction: () => void;
};

const selectIdleCore = (s: LiveGameState) => ({
  tournamentId: s.tournament.id,
  isArena: s.tournament.mode === 'arena',
  // Fix K: `rail/surface.ts`'s one definition of "idle" (`railState ===
  // 'tournament'`), rather than a second copy of the same three fields —
  // provably the same boolean (`__tests__/surfaceMatrix.test.tsx`).
  idle: showTournamentIdle(s),
  // Fix T / T1: the whole slices, fed to `statusInputFrom` — a reducer only
  // replaces either reference when something inside it changes, never on a
  // plain game move, so this stays 0 renders per opponent move.
  tournament: s.tournament,
  arena: s.arena,
});

export function useTournamentIdleModel(): TournamentIdleModel {
  const core = useLiveGame(useShallow(selectIdleCore));
  const setRailTab = useV2Ui(s => s.setRailTab);
  const arenaResume = useLiveGame(s => s.actions.arenaResume);
  const arenaPause = useLiveGame(s => s.actions.arenaPause);

  const status = tournamentStatusFrom(
    statusInputFrom(
      {tournament: core.tournament, arena: core.arena},
      {raw: null, nowMs: nowServerMs(), myGameLive: false, opponentName: null},
    ),
  );
  // the hook must run unconditionally (every render, same order) even while
  // `!core.idle` — harmless: `secondsLeft` is simply never read in that case.
  const countdownSecs = useCountdownSeconds(
    core.idle ? status.countdownTo : null,
  );

  if (!core.tournamentId || !core.idle) {
    return {
      kind: 'none',
      title: '',
      subtitle: '',
      actionLabel: null,
      onAction: () => undefined,
    };
  }

  if (status.phase === 'ended') {
    return {
      kind: 'ended',
      title: status.headline,
      subtitle: '',
      // T5: arena-aware — the Details tab's own CTA reads "See leaderboard"
      // for an arena; this card's button now says the same thing.
      actionLabel: core.isArena ? 'See leaderboard' : 'See standings',
      onAction: () => setRailTab(resultTabFor(core.isArena)),
    };
  }
  // T5: the arena's own between-games states.
  if (status.phase === 'paused') {
    return {
      kind: 'paused',
      title: status.headline,
      subtitle: '',
      actionLabel: 'Resume',
      onAction: arenaResume,
    };
  }
  if (status.phase === 'pooled') {
    return {
      kind: 'pooled',
      title: status.headline,
      subtitle: '',
      actionLabel: 'Pause',
      onAction: arenaPause,
    };
  }
  // T5: an arena's "not in the pool" gets the "Join the pool" CTA — the
  // same action the old `ArenaLobby`'s own button calls (L333-345). A
  // Swiss `sittingOut` (the round is live and I am not in it) has no such
  // button and falls through to the generic branch below, unchanged.
  if (status.phase === 'sittingOut' && core.isArena) {
    return {
      kind: 'sittingOut',
      title: status.headline,
      subtitle: '',
      actionLabel: 'Join the pool',
      onAction: arenaResume,
    };
  }
  if (status.phase === 'bye') {
    return {
      kind: 'bye',
      title: status.headline,
      // fix AA (2026-10-06): the detail line carries "a free point · paired
      // again in round N" — it used to be dropped on the floor here.
      subtitle: status.detail ?? '',
      actionLabel: null,
      onAction: () => undefined,
    };
  }
  if (status.phase === 'notJoined') {
    return {
      kind: 'notJoined',
      title: status.headline,
      subtitle: '',
      actionLabel: 'Register from Details',
      onAction: () => {
        armRegistrationIntent(core.tournamentId as string);
        setRailTab('details');
      },
    };
  }

  // every other phase (registered / awaitingArbiter / pairing / paired /
  // sittingOut / break / withdrawn / playing / ending / a Swiss sittingOut):
  // the same headline + a simple countdown or the status detail, no special
  // CTA (S7: "keep its existing CTAs").
  //
  // T0.3 (Fix S review #3): `formatCountdown` instead of a bare `${secs}s`,
  // so a long wait (e.g. a 185 s break) reads "3:05" here exactly as it
  // does on the Details card (acceptance #9 of Fix S) — retires S-x4.
  const subtitle =
    countdownSecs !== null && countdownSecs > 0
      ? `${status.countdownLabel ?? ''} ${formatCountdown(
          countdownSecs,
        )}`.trim()
      : status.detail ?? '';

  return {
    kind: status.phase,
    title: status.headline,
    subtitle,
    actionLabel: null,
    onAction: () => undefined,
  };
}

export default useTournamentIdleModel;
